/**
 * useProposeChore — kid suggests a chore (Fase 3).
 *
 * POST /api/chores/propose (requireAuth — any signed-in member). The suggestion
 * lands as status='draft_proposed' and never spawns until a parent approves it
 * in the Approvals tab. The body matches the server's ProposeBody Zod:
 *   { title, description?, icon_key, point_value, recurrence }
 *
 * Server-authoritative: no optimistic UI. On success we surface a warm toast so
 * the kid knows the suggestion went to a parent — it fires in the same tick as
 * the sheet's onClose(), so it lands on an unobstructed screen.
 *
 * There is deliberately NO onError toast (#74): the sheet stays open on failure,
 * and ToastViewport (z-40) sits behind the sheet overlay (z-50), so the kid
 * would never see it. ProposeChoreSheet renders the error inline instead; the
 * typed error is re-thrown, so a per-call onError sees it.
 */
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Recurrence } from '@stell/shared';
import { toast } from '../../lib/toast';

export interface ProposeChoreInput {
  title: string;
  description?: string;
  icon_key: string;
  point_value: number;
  recurrence: Recurrence;
}

interface FetchError extends Error {
  status: number;
}

export function useProposeChore() {
  const { t } = useTranslation();
  return useMutation<unknown, FetchError, ProposeChoreInput>({
    mutationFn: async (input) => {
      const r = await globalThis.fetch('/api/chores/propose', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const err = new Error(`fetch_failed:${r.status}`) as FetchError;
        err.status = r.status;
        throw err;
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ kind: 'info', body: t('chores.propose.success') });
    },
  });
}
